import { useRef, type ReactNode } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { PrivateRoute } from "@/components/auth/private-route";
import { PublicRoute } from "@/components/auth/public-route";
import { OptionalAuthRoute } from "@/components/auth/optional-auth-route";
import { AppGateSplash } from "@/components/common/AppGateSplash";
import {
  ONBOARDING_PATH,
  PERSONA_SETUP_PATH,
  usePostLoginGate,
} from "@/features/persona-setup/hooks/use-post-login-gate";
import MainLayout from "@/layouts/MainLayout";

/**
 * Data router "layout route" element'leri. Path'siz route olarak kullanılır;
 * altındaki child'lar `<Outlet />` üzerinden render edilir. Guard bileşenlerinin
 * kendileri değişmedi — yalnızca children yerine Outlet alıyorlar.
 */

/** Sidebar + header kabuğu. */
export const ShellLayout = () => (
  <MainLayout>
    <Outlet />
  </MainLayout>
);

export const PrivateOutlet = () => (
  <PrivateRoute>
    <Outlet />
  </PrivateRoute>
);

export const OptionalAuthOutlet = () => (
  <OptionalAuthRoute>
    <Outlet />
  </OptionalAuthRoute>
);

/** Yalnızca oturumsuz kullanıcı: login/register vb. */
export const GuestOutlet = () => (
  <PublicRoute>
    <Outlet />
  </PublicRoute>
);

// ─── Persona / onboarding gate'leri (App.tsx'ten taşındı) ────────────────────

export function OnboardingRouteGuard({ children }: { children: ReactNode }) {
  const gate = usePostLoginGate();

  if (gate.isGateLoading) {
    return <AppGateSplash />;
  }

  if (gate.hasPendingAgreements) {
    return <>{children}</>;
  }

  if (gate.shouldShowPersonaSetup) {
    return <Navigate to={PERSONA_SETUP_PATH} replace />;
  }

  if (!gate.shouldShowOnboarding) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}

export function PersonaSetupRouteGuard({ children }: { children: ReactNode }) {
  const gate = usePostLoginGate();
  const enteredRef = useRef(false);

  // İlk girişte (veriler yüklendiyse, sözleşme yoksa) içeri al; sonraki
  // profil kayıtları hasPending'i titretse bile sayfayı sökme.
  // isGateLoading şartı: hard refresh'te /me gelmeden stale user ile
  // mount olup boş prefill / yanlış adım seed'i oluşmasın.
  if (
    !gate.isGateLoading &&
    !gate.hasPendingAgreements &&
    (gate.shouldShowPersonaSetup || gate.isPersonaComplete)
  ) {
    enteredRef.current = true;
  }

  if (enteredRef.current) {
    return <>{children}</>;
  }

  if (gate.isGateLoading) {
    return <AppGateSplash />;
  }

  if (gate.hasPendingAgreements) {
    return <Navigate to="/" replace />;
  }

  if (gate.shouldShowPersonaSetup || gate.isPersonaComplete) {
    enteredRef.current = true;
    return <>{children}</>;
  }

  if (gate.shouldShowOnboarding) {
    return <Navigate to={ONBOARDING_PATH} replace />;
  }

  return <Navigate to="/" replace />;
}
